import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStudents } from './StudentProvider';
import studentsService from '../../services/studentsService';
import classSessionsService from '../../services/classSessionsService';
import { 
  ArrowLeft, 
  Edit, 
  Copy, 
  Trash2, 
  Calendar, 
  Star, 
  Users,
  Mail,
  Phone,
  User,
  Target,
  BookOpen,
  AlertCircle,
  CheckCircle,
  Clock,
  Plus
} from 'lucide-react';

const StudentView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { students, getStudent, deleteStudent, cloneStudent, loading } = useStudents();
  const [student, setStudent] = useState(null);
  const [studentLoading, setStudentLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [enrollmentSessions, setEnrollmentSessions] = useState({});

  useEffect(() => {
    const loadStudent = async () => {
      try {
        setStudentLoading(true);
        setError(null);
        
        console.log('StudentView - Loading student with ID:', id);
        
        // Always fetch fresh data from API to get enrollment details
        const fetchedStudent = await studentsService.getStudent(id);
        console.log('StudentView - Fetched student with enrollments:', fetchedStudent);
        
        if (fetchedStudent) {
          setStudent(fetchedStudent);
        } else {
          setError('Student not found');
        }
      } catch (err) {
        console.error('StudentView - Error loading student:', err);
        setError('Failed to load student');
      } finally {
        setStudentLoading(false);
      }
    };

    if (id) {
      loadStudent();
    }
  }, [id]);

  // Load sessions for all enrollments
  useEffect(() => {
    const loadEnrollmentSessions = async () => {
      if (!student || !student.enrollments) return;
      
      const sessionsMap = {};
      
      for (const enrollment of student.enrollments) {
        const classId = enrollment.classId || enrollment.class_id;
        if (!classId || sessionsMap[classId]) continue;
        
        try {
          const result = await classSessionsService.getSessionsForClass(classId);
          if (result.success) {
            sessionsMap[classId] = result.data || [];
          }
        } catch (error) {
          console.error('Failed to load sessions for class:', classId, error);
          sessionsMap[classId] = [];
        }
      }
      
      setEnrollmentSessions(sessionsMap);
    };
    
    loadEnrollmentSessions();
  }, [student]);

  const handleEdit = () => {
    navigate(`/students/edit/${id}`);
  };

  const handleClone = async () => {
    try {
      await cloneStudent(student.id);
      navigate('/students');
    } catch (error) {
      console.error('Error cloning student:', error);
      setError('Failed to clone student. Please try again.');
    }
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteStudent(student.id);
      navigate('/students');
    } catch (error) {
      console.error('Error deleting student:', error);
      setError('Failed to delete student. Please try again.');
    }
    setShowDeleteModal(false);
  };

  // Format session information similar to ClassCard
  const formatSessionInfo = (sessions) => {
    if (!sessions || sessions.length === 0) {
      return 'No sessions scheduled';
    }
    
    // Sort sessions by date
    const sortedSessions = [...sessions].sort((a, b) => new Date(a.session_date) - new Date(b.session_date));
    const startDate = new Date(sortedSessions[0].session_date);
    const sessionCount = sessions.length;
    
    return `${sessionCount} session${sessionCount !== 1 ? 's' : ''}, starts ${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  const getExperienceColor = (experience) => {
    switch (experience?.toLowerCase()) {
      case 'advanced': return 'bg-[#FFC6C5] dark:bg-red-600 text-[#3C3C3C] dark:text-gray-100';
      case 'intermediate': return 'bg-[#FFECC5] dark:bg-yellow-600 text-[#3C3C3C] dark:text-gray-100';
      case 'beginner': return 'bg-[#B7F1B6] dark:bg-green-600 text-[#3C3C3C] dark:text-gray-100';
      case 'none': return 'bg-[#D9D9D9] dark:bg-gray-600 text-[#3C3C3C] dark:text-gray-100';
      default: return 'bg-[#D9D9D9] dark:bg-gray-600 text-[#3C3C3C] dark:text-gray-100';
    }
  };

  // Get gender-specific placeholder background
  const getGenderPlaceholderStyle = () => {
    const gender = student.gender || '';
    switch (gender?.toLowerCase()) {
      case 'female':
        return 'bg-gradient-to-br from-pink-300 to-pink-500';
      case 'male':
        return 'bg-gradient-to-br from-blue-300 to-blue-500';
      default:
        return 'bg-gradient-to-br from-gray-300 to-gray-500';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-emerald-100 text-emerald-700';
      case 'completed': return 'bg-[#7A99D8] bg-opacity-20 text-[#7A99D8]';
      case 'suspended': return 'bg-red-100 text-red-700';
      case 'cancelled': return 'bg-[#F5F5F5] text-[#3C3C3C] dark:text-gray-100';
      default: return 'bg-[#F5F5F5] text-[#3C3C3C] dark:text-gray-100';
    }
  };

  if (studentLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center space-x-3">
            <User className="w-8 h-8 text-[#7A99D8]" />
            <div>
              <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-gray-100">Student Details</h1>
              <p className="text-sm text-gray-600 dark:text-gray-300">Loading student information...</p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-gray-200 dark:border-gray-700 border-t-[#7A99D8] rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-300">Loading student...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !student) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <User className="w-8 h-8 text-[#7A99D8]" />
              <div>
                <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-gray-100">Student Not Found</h1>
                <p className="text-sm text-gray-600 dark:text-gray-300">The requested student could not be found</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/students')}
              className="bg-[#F5F5F5] hover:bg-gray-200 text-[#3C3C3C] dark:text-gray-100 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Students
            </button>
          </div>
        </div>
        
        <div className="px-6 py-6">
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <div className="w-24 h-24 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-[#3C3C3C] dark:text-gray-100 mb-3">Student Not Found</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-md mx-auto">
              {error || "The student you're looking for doesn't exist or may have been removed."}
            </p>
            <button
              onClick={() => navigate('/students')}
              className="bg-[#FFECC5] hover:bg-[#FFE4A3] text-[#3C3C3C] dark:text-gray-100 px-6 py-3 rounded-lg transition-colors font-medium"
            >
              Back to Students
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/students')}
              className="p-2 text-gray-400 dark:text-gray-500 hover:text-[#7A99D8] dark:hover:text-[#8FAEE6] rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <User className="w-8 h-8 text-[#7A99D8]" />
            <div>
              <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-gray-100">
                {student.studentFirstName || student.student_first_name || student.firstName || ''} {student.studentLastName || student.student_last_name || student.lastName || ''}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-300">Student Profile & Information</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handleEdit}
              className="bg-[#F5F5F5] hover:bg-gray-200 text-[#3C3C3C] dark:text-gray-100 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors font-medium"
            >
              <Edit className="w-4 h-4" />
              Edit
            </button>
            <button
              onClick={handleClone}
              className="bg-[#F5F5F5] hover:bg-gray-200 text-[#3C3C3C] dark:text-gray-100 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors font-medium"
            >
              <Copy className="w-4 h-4" />
              Clone
            </button>
            <button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors font-medium"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </div>
      </div>

      <div className="px-6 py-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          {/* Student Header */}
          <div className="flex items-start gap-6 mb-8">
            {/* Student Photo */}
            <div className="flex-shrink-0">
              {student.studentPhoto || student.student_photo || student.photo || student.photoUrl || student.photo_url ? (
                <img
                  src={student.studentPhoto || student.student_photo || student.photo || student.photoUrl || student.photo_url}
                  alt={`${student.studentFirstName || student.student_first_name || student.firstName || ''} ${student.studentLastName || student.student_last_name || student.lastName || ''}`}
                  className="w-24 h-24 rounded-lg object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div 
                className={`w-24 h-24 ${getGenderPlaceholderStyle()} rounded-lg flex items-center justify-center text-white text-2xl font-medium`}
                style={{ display: (student.studentPhoto || student.student_photo || student.photo || student.photoUrl || student.photo_url) ? 'none' : 'flex' }}
              >
                {(student.studentFirstName || student.student_first_name || student.firstName || '').charAt(0)}{(student.studentLastName || student.student_last_name || student.lastName || '').charAt(0)}
              </div>
            </div>

            {/* Student Basic Info */}
            <div className="flex-1">
              <h2 className="text-2xl font-semibold text-[#3C3C3C] dark:text-gray-100 dark:text-gray-100 mb-3">
                {student.studentFirstName || student.student_first_name || student.firstName || ''} {student.studentLastName || student.student_last_name || student.lastName || ''}
              </h2>
              
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  <strong>Age:</strong> {student.studentAge || student.student_age || student.age || 'N/A'} years old
                </span>
                
                {student.gender && (
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    <strong>Gender:</strong> {student.gender}
                  </span>
                )}
                
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  <strong>Added:</strong> {new Date(student.dateAdded || student.date_added || student.created_at || Date.now()).toLocaleDateString()}
                </span>
              </div>

              {/* Skill Level */}
              <div className="mb-4">
                <span className="text-sm text-gray-600 dark:text-gray-300 mr-2"><strong>Skill Level:</strong></span>
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${getExperienceColor(student.experience || '')}`}>
                  {student.experience || 'Not specified'}
                </span>
              </div>
            </div>
          </div>

          {/* Parent/Guardian Information */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100 dark:text-gray-100 mb-4">Parent/Guardian Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-[#F5F5F5] dark:bg-gray-700 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white dark:bg-gray-600 rounded-lg flex items-center justify-center">
                    <User className="w-4 h-4 text-[#7A99D8]" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-[#3C3C3C] dark:text-gray-100 dark:text-gray-100">
                      {student.parentName || student.parent_name || 'Not provided'}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300">Parent/Guardian</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-[#F5F5F5] dark:bg-gray-700 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white dark:bg-gray-600 rounded-lg flex items-center justify-center">
                    <Mail className="w-4 h-4 text-[#7A99D8]" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-[#3C3C3C] dark:text-gray-100 dark:text-gray-100">
                      {student.parentEmail || student.parent_email || 'Not provided'}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300">Email Address</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-[#F5F5F5] dark:bg-gray-700 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white dark:bg-gray-600 rounded-lg flex items-center justify-center">
                    <Phone className="w-4 h-4 text-[#7A99D8]" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-[#3C3C3C] dark:text-gray-100 dark:text-gray-100">
                      {student.parentPhone || student.parent_phone || 'Not provided'}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300">Phone Number</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Enrollment History */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100 dark:text-gray-100 mb-4">Enrollment History</h3>
            {(() => {
              // Handle both backend data structure (enrollments) and sample data (enrollmentHistory)
              const enrollmentData = student.enrollments || student.enrollmentHistory || [];
              
              if (enrollmentData.length > 0) {
                return (
                  <div className="space-y-3">
                    {enrollmentData.map((enrollment, index) => {
                      // Handle different data structures
                      const enrollmentDate = new Date(
                        enrollment.enrollmentDate || 
                        enrollment.enrollment_date || 
                        enrollment.enrolledDate
                      );
                      const className = enrollment.className || enrollment.class_name || enrollment.class || 'Unknown Class';
                      const isActive = (enrollment.enrollmentStatus || enrollment.status) === 'active';
                      const classId = enrollment.classId || enrollment.class_id;
                      const sessions = enrollmentSessions[classId] || [];
                      
                      return (
                        <div key={index} className="flex items-center gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                          <div className={`w-10 h-10 ${isActive ? 'bg-emerald-100' : 'bg-gray-100'} rounded-lg flex items-center justify-center`}>
                            <BookOpen className={`w-5 h-5 ${isActive ? 'text-emerald-600' : 'text-gray-500'}`} />
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-[#3C3C3C] dark:text-gray-100 dark:text-gray-100">{className}</div>
                            <div className="text-sm text-gray-600 dark:text-gray-300">
                              Enrolled: {enrollmentDate.toLocaleDateString()}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-300">
                              Class dates: {formatSessionInfo(sessions)}
                            </div>
                          </div>
                          <span className={`px-2 py-1 ${
                            isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'
                          } text-xs font-medium rounded-full`}>
                            {isActive ? 'Active' : 'Completed'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              } else {
                return (
                  <div className="bg-[#F5F5F5] dark:bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-200 dark:bg-gray-600 rounded-lg flex items-center justify-center">
                        <AlertCircle className="w-5 h-5 text-gray-500 dark:text-gray-300" />
                      </div>
                      <div>
                        <div className="font-medium text-[#3C3C3C] dark:text-gray-100 dark:text-gray-100">No Enrollment History</div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                          This student has never been enrolled in any classes.
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
            })()}
          </div>

          {/* Action Button */}
          <div className="flex justify-center">
            <button
              onClick={() => navigate('/enrollments/add')}
              className="bg-[#FFECC5] hover:bg-[#FFE4A3] text-[#3C3C3C] dark:text-gray-100 px-6 py-3 rounded-lg transition-colors font-medium inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Enroll in Class
            </button>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-70 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full mx-4">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <AlertCircle className="w-6 h-6 text-red-500 mr-3" />
                  <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100 dark:text-gray-100">Delete Student</h3>
                </div>
                
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  Are you sure you want to delete this student? This action cannot be undone.
                </p>
                
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-[#3C3C3C] dark:text-gray-100 dark:text-gray-100 rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentView;